import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { flowRight, words } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";
import { TypeAttributes } from "rsuite/esm/internals/types";
import {
    Button,
    ButtonToolbar,
    Container,
    Content,
    FlexboxGrid,
    Footer as RsuiteFooter,
    FormInstance,
    Header,
    Heading,
    Message,
    Nav,
    Text,
    toaster,
    Toggle,
} from "rsuite";

import i18n from "utils/i18n";
import constants from "utils/constants";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import { getTenantId } from "store/environments/tenants/tenants-selectors";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import { CustomField } from "store/environments/tenants/custom-fields/dooap-types";

import {
    customField1,
    customField2,
    customField3,
    defaultValue,
} from "../custom-fields-helpers";
import { ConfluenceLinks } from "store/information-links/information-links-types";

import { getConfluenceDocumentLinks } from "store/information-links/information-links-selectors";
import { fetchEnvironments } from "store/environments/environment-thunks";
import { fetchTenants } from "store/environments/tenants/tenants-thunks";
import { fetchConfluenceDocumentLinks } from "store/information-links/information-links-thunks";

import { getErrorMessage } from "api/defaults";
import {
    loadCustomFields,
    updateCustomFields,
    removeCustomFields,
} from "api/environments/tenants/custom-fields/dooap";

import useRequestCancellation from "hooks/useRequestCancellation";
import useRefreshPage from "hooks/useRefreshPage";

import withLoader from "hocs/withLoader/withLoader";
import withError from "hocs/withError/withError";

import { checkCustomFieldsInitialized } from "../custom-fields-helpers";

import Form, { FormItem } from "./Form/Form";
import Footer from "components/Common/Footer/Footer";

import "./CustomFields.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;

    showLoader?: (loaderVisibility: boolean) => void;
    showError?: (error: string) => void;
}

const CustomFields: React.FC<Props> = ({
    title = i18n.t("tenantConfig.customFieldsPage.header"),
    showLoader,
    showError,
}) => {
    const tab1 = customField1;
    const tab2 = customField2;
    const tab3 = customField3;

    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();
    const params: Record<string, string> = useParams<{
        environmentId: string;
        tenantId: string;
    }>();

    const ref1 = useRef<FormInstance<Record<string, any>>>(null);
    const ref2 = useRef<FormInstance<Record<string, any>>>(null);
    const ref3 = useRef<FormInstance<Record<string, any>>>(null);

    const refs = new Map<
        string,
        React.RefObject<FormInstance<Record<string, any>>>
    >();

    refs.set(tab1, ref1);
    refs.set(tab2, ref2);
    refs.set(tab3, ref3);

    const dispatch = useAppDispatch();

    const { environmentId, tenantId } = params;

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const confluenceDocumentLinks = useSelector(
        (state: RootState): ConfluenceLinks => getConfluenceDocumentLinks(state)
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [isOn, setIsOn] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const [customFields, setCustomFields] =
        useImmer<ReadonlyArray<CustomField>>(defaultValue);

    const [customFieldFormItems, setCustomFieldFormItems] = useImmer<
        FormItem<CustomField>[]
    >(defaultValue.map((field) => ({ ...field, isValid: true })));

    const [selectedCustomField, setSelectedCustomField] = useImmer<CustomField>(
        defaultValue[0]
    );

    const [submitPermitted, setSubmitPermitted] = useState<boolean>(true);

    const tabIndex = customFields.findIndex(
        (field) => field.code === selectedCustomField.code
    );

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([fetchCustomFields()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchCustomFields = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadCustomFields(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                const initialized = checkCustomFieldsInitialized(payload.data);

                setCustomFields(payload.data);
                setCustomFieldFormItems(
                    payload.data.map((field) => ({
                        ...field,
                        uiColumnName: initialized
                            ? field.uiColumnName
                            : field.databaseColumnName,
                        isValid: true,
                    }))
                );

                setSelectedCustomField(payload.data[0]);
                setInitialized(initialized);
                setIsOn(initialized);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const changeCustomFields = async () => {
        if (!isOn) {
            return;
        }

        try {
            const [payload, status] = await updateCustomFields(
                activeEnvironmentId,
                activeTenantId,
                customFieldFormItems,
                cancelTokenSource
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const deleteCustomFields = async () => {
        if (isOn) {
            return;
        }

        try {
            const [payload, status] = await removeCustomFields(
                activeEnvironmentId,
                activeTenantId,
                customFieldFormItems,
                cancelTokenSource
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSubmit = () => {
        if (!submitPermitted) {
            return;
        }

        showLoader?.(true);

        const promises = [changeCustomFields(), deleteCustomFields()];

        Promise.all(promises).then((value) => {
            showLoader?.(false);
            dispatch(setIsUnsavedChangeAvailable(false));

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = t("tenantConfig.customFieldsPage.successSent");

                fetchData();
            }

            toaster.push(
                <Message showIcon type={type}>
                    {message}
                </Message>,
                toastOptions
            );
        });
    };

    const handleFormChange = (data: Record<string, any>) => {
        const updated = data as FormItem<CustomField>;

        setCustomFieldFormItems((draft) => {
            const field = draft.find((f) => f.code === updated.code);
            if (field) {
                Object.assign(field, updated);
            } else {
                draft.push({ ...updated });
            }
        });

        setSelectedCustomField(updated);

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleToggleChange = (checked: boolean) => {
        setIsOn(checked);
    };

    const handleTabSelect = (key: string) => {
        const customField = customFields.find((field) => field.code === key);

        if (customField) {
            setSelectedCustomField(customField);
        }
    };

    const handleNextClick = () => {
        if (tabIndex >= customFields.length - 1) {
            return;
        }

        setSelectedCustomField(customFields[tabIndex + 1]);
    };

    const handleBackClick = () => {
        if (tabIndex <= 0) {
            return;
        }

        setSelectedCustomField(customFields[tabIndex - 1]);
    };

    const validateForm = (data: CustomField) => {
        const ref = refs.get(data.code)!;

        ref?.current?.check();
    };

    const clearFormErrors = (data: CustomField) => {
        const ref = refs.get(data.code)!;

        ref.current?.cleanErrors();
    };

    useEffect(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, activeTenantId]);

    useRefreshPage(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (!confluenceDocumentLinks) {
            fetchConfluenceDocumentLinks();
        }
    });

    useEffect(() => {
        if (!activeEnvironmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!activeTenantId && activeEnvironmentId) {
            dispatch(fetchTenants(activeEnvironmentId, tenantId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!isOn) {
            clearFormErrors(selectedCustomField);
        } else {
            validateForm(selectedCustomField);
        }
    }, [selectedCustomField, isOn]);

    useEffect(() => {
        const any = customFields.length > 0;

        const valid = customFieldFormItems.every((item) => item.isValid);

        const permitted = dataFetched && any && valid;

        setSubmitPermitted(permitted);
    }, [dataFetched, customFields, customFieldFormItems]);

    const tabs: React.JSX.Element[] = customFields.map((field) => {
        const name = words(field.code).join(" ");

        return (
            <Nav.Item key={field.code} eventKey={field.code} disabled={!isOn}>
                {name}
            </Nav.Item>
        );
    });

    const forms: React.JSX.Element[] = customFieldFormItems.map((field) => {
        const ref = refs.get(selectedCustomField.code)!;

        return (
            <Form
                key={field.code}
                style={{ margin: "2rem 0 0" }}
                formRef={ref}
                customField={field}
                disabled={!isOn}
                onChange={(formValue) => handleFormChange(formValue)}
            />
        );
    });

    const selectedForm = forms.find(
        (form) => form.key === selectedCustomField.code
    ) ?? <></>;

    const footer = (
        <ButtonToolbar>
            {!initialized &&
                isOn &&
                tabIndex > 0 &&
                tabIndex <= customFields.length - 1 && (
                    <Button
                        className="custom-fields__footer__btn"
                        type="submit"
                        appearance="ghost"
                        onClick={() => handleBackClick()}
                    >
                        {t("tenantConfig.customFieldsPage.buttonBack")}
                    </Button>
                )}

            {!initialized && isOn && tabIndex < customFields.length - 1 && (
                <Button
                    className="custom-fields__footer__btn"
                    appearance="primary"
                    onClick={() => handleNextClick()}
                >
                    {t("tenantConfig.customFieldsPage.buttonNext")}
                </Button>
            )}

            {(initialized ||
                (isOn && tabIndex === customFields.length - 1)) && (
                <Button
                    className="custom-fields__footer__btn"
                    appearance="primary"
                    disabled={!submitPermitted}
                    onClick={() => handleSubmit()}
                >
                    {t("tenantConfig.customFieldsPage.buttonSave")}
                </Button>
            )}
        </ButtonToolbar>
    );

    return (
        dataFetched && (
            <Container className="custom-fields__container">
                <Header>
                    <Heading level={1}>{title}</Heading>
                </Header>

                <Content className="custom-fields__content">
                    <FlexboxGrid
                        justify={"start"}
                        align={"middle"}
                        style={{ margin: "1rem 0 0" }}
                    >
                        <FlexboxGrid.Item style={{ marginRight: "0.75rem" }}>
                            <Toggle
                                checked={isOn}
                                className={"custom-fields__toggle"}
                                checkedChildren={t("toggler.onTitle")}
                                unCheckedChildren={t("toggler.offTitle")}
                                onChange={handleToggleChange}
                            />
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item>
                            <Text>
                                {t(
                                    "tenantConfig.customFieldsPage.toggleControl"
                                )}
                            </Text>
                        </FlexboxGrid.Item>
                    </FlexboxGrid>

                    <Nav
                        style={{ margin: "1.5rem 0 0" }}
                        appearance="subtle"
                        onSelect={(key) => handleTabSelect(key)}
                        activeKey={selectedCustomField.code}
                        className={"custom-fields__tab-bar"}
                    >
                        {tabs}
                    </Nav>

                    <Message style={{ margin: "1rem 0" }} showIcon type="info">
                        <>
                            {t(
                                "tenantConfig.customFieldsPage.table.skipFieldTooltip"
                            )}
                            <br />
                            {t(
                                "tenantConfig.customFieldsPage.table.confluenceLinkStart"
                            )}
                            <a
                                className="custom-fields__message__link"
                                href={
                                    confluenceDocumentLinks.customFieldsConfiguration
                                }
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                {t(
                                    "tenantConfig.customFieldsPage.table.confluenceLinkEnd"
                                )}
                            </a>
                        </>
                    </Message>
                    {selectedForm}
                </Content>

                <RsuiteFooter>
                    <Footer customButton={footer} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError)(CustomFields);

export default Component;
